/**
 * Property limits for `track()`, applied in the browser before anything is uploaded.
 *
 * The contract lives in `tests/fixtures/work-properties/contract.json`, which this module's
 * test, the consumer's (`sanitizeProperties`) and the Rust parser's (`extract_product_events`)
 * all read — three doors, one set of limits.
 */

/**
 * The same three limits the aggregate applies — but applied HERE, at the door.
 *
 * They used to live only in the consumer, so the row was clean while the PAYLOAD went out as
 * given: `track('x', { form: { email, otp } })` reached the upload and was stored raw in R2
 * (found 2026-09-10). A promise about content has to hold where the content is handed over.
 * Kept in sync with `PRODUCT_EVENT_LIMITS` in the consumer and the Rust parser's constants.
 */
export const TRACK_MAX_PROPERTIES = 25;
export const TRACK_MAX_VALUE_LENGTH = 100;
export const TRACK_MAX_KEY_LENGTH = 64;
/** These four carry meaning of their own and are never counted as properties. */
const TRACK_RESERVED_KEYS = new Set(['event.name', 'event.label', 'user.id', 'perspectify.internal']);
/**
 * The work-session keys Perspectify reads (usage-history.ts). They sort under "w", after nearly
 * every other key, so a 30-property event used to lose exactly these to the budget and fall
 * back to the 30-minute rule. They are kept OUTSIDE the budget — still scalars, still at most
 * 64 / 100 characters, so an event stays bounded. Any other `work.*` key is an ordinary property.
 */
/** How a run ended, how long it took, and its tool calls — see `runMetric` below. */
const TRACK_RUN_METRIC_KEYS: ReadonlySet<string> = new Set([
  'work.agent.run.duration_ms',
  'work.agent.run.status',
  'work.agent.run.tool_calls',
]);
export const PROTECTED_WORK_PROPERTIES: ReadonlySet<string> = new Set([
  'work.agent.id',
  'work.agent.parent.run.id',
  'work.agent.run.id',
  'work.event.origin',
  'work.message.id',
  'work.message.role',
  'work.session.id',
  'work.session.keywords',
  'work.session.kind',
  ...TRACK_RUN_METRIC_KEYS,
  'work.event.quantity',
]);

/**
 * How much one event did — `work-event-quantity.ts` in the Worker, the same contract: a JS number
 * that is a whole number from 0 to 1 000 000, kept as that number. A fraction, a negative, a larger
 * value, a string (also "42"), a boolean or anything else drops the key whole; the event is still
 * sent. Missing is not zero. Any other name, `count` included, stays an ordinary property.
 */
const TRACK_QUANTITY_KEY = 'work.event.quantity';
const TRACK_MAX_QUANTITY = 1_000_000;

function eventQuantity(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > TRACK_MAX_QUANTITY) {
    return undefined;
  }
  return value === 0 ? 0 : value;
}

/**
 * How an agent run ended, how long it took, and its tool calls by kind — closed values and
 * bounded numbers only. The same rules as `work-run-metrics.ts` in the Worker (kept in step by
 * the shared contract): a value is stored as sent (the duration as a number) or dropped whole,
 * never cut into something that reads differently.
 */
const TRACK_RUN_STATUSES = new Set(['completed', 'failed', 'canceled']);
const TRACK_RUN_TOOL_CATEGORIES = new Set(['search', 'retrieve', 'write', 'execute', 'other']);
const TRACK_MAX_RUN_DURATION_MS = 604_800_000;
const TRACK_MAX_RUN_TOOL_CALLS = 10_000;

function boundedWholeNumber(text: string, max: number): number | undefined {
  if (!/^(0|[1-9][0-9]*)$/.test(text) || text.length > String(max).length) return undefined;
  const value = Number(text);
  return value <= max ? value : undefined;
}

function runMetric(key: string, value: unknown): string | number | undefined {
  if (key === 'work.agent.run.status') {
    return typeof value === 'string' && TRACK_RUN_STATUSES.has(value) ? value : undefined;
  }
  if (key === 'work.agent.run.duration_ms') {
    if (typeof value === 'number') {
      return Number.isInteger(value) && value >= 0 && value <= TRACK_MAX_RUN_DURATION_MS ? value : undefined;
    }
    return typeof value === 'string' ? boundedWholeNumber(value, TRACK_MAX_RUN_DURATION_MS) : undefined;
  }
  if (key === 'work.agent.run.tool_calls') {
    if (typeof value !== 'string' || value.length === 0 || value.length > TRACK_MAX_VALUE_LENGTH) return undefined;
    const parts = value.split(',');
    if (parts.length > TRACK_RUN_TOOL_CATEGORIES.size) return undefined;
    const seen = new Set<string>();
    for (const part of parts) {
      const match = /^([a-z]+):(0|[1-9][0-9]{0,4})$/.exec(part);
      if (!match || !TRACK_RUN_TOOL_CATEGORIES.has(match[1]) || seen.has(match[1])) return undefined;
      if (boundedWholeNumber(match[2], TRACK_MAX_RUN_TOOL_CALLS) === undefined) return undefined;
      seen.add(match[1]);
    }
    return value;
  }
  return undefined;
}
// eslint-disable-next-line no-control-regex
const TRACK_CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
const TRACK_EMAIL_LIKE = /[^\s"'<>(),;:]+@[^\s"'<>(),;:]+\.[a-z]{2,}/i;

export function trackProperties(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!properties) return undefined;
  const flach: Record<string, unknown> = {};
  let gezaehlt = 0;
  // Sorted keys, so dropping the tail is the same on every call rather than insertion-dependent.
  for (const key of Object.keys(properties).sort()) {
    // Usage-only metadata must never enter a session recording.
    if (key === 'work.session.purpose') continue;
    // Reserved and protected keys are checked like any other, but never use up the budget.
    const outsideBudget = TRACK_RESERVED_KEYS.has(key) || PROTECTED_WORK_PROPERTIES.has(key);
    // RESERVED KEYS DO NOT USE UP THE BUDGET. `event.label` sorts under "e" and 25 properties
    // with earlier names used to push it out here — silently, in the browser, while the
    // consumer takes the reserved keys out BEFORE its cap. Two doors, two strictnesses.
    if (!outsideBudget && gezaehlt >= TRACK_MAX_PROPERTIES) continue;
    // A property NAME is a category: short, no control characters (see PRODUCT_EVENT_LIMITS).
    if (key.length === 0 || key.length > TRACK_MAX_KEY_LENGTH || TRACK_CONTROL_CHARACTERS.test(key)) continue;
    const value = properties[key];
    if (TRACK_RUN_METRIC_KEYS.has(key)) {
      const metric = runMetric(key, value);
      if (metric !== undefined) flach[key] = metric;
      continue;
    }
    if (key === TRACK_QUANTITY_KEY) {
      const quantity = eventQuantity(value);
      if (quantity !== undefined) flach[key] = quantity;
      continue;
    }
    if (typeof value === 'string') {
      // ENTSCHAERFEN, DANN PRUEFEN — in dieser Reihenfolge, und das ist der ganze Befund.
      //
      // Vorher lief die E-Mail-Pruefung auf dem ROHEN Wert und das Entfernen der Steuerzeichen
      // danach. `anna@\nexample.com` brach damit das Muster (der Ausdruck schliesst `\s` aus),
      // passierte den Riegel — und die naechste Zeile machte daraus die versendbare Adresse
      // `anna@example.com`, die hochgeladen und in R2 gespeichert wurde. Gemessen am
      // 2026-09-10 im hochgeladenen Koerper; betroffen waren genau die fuenf Steuerzeichen,
      // die auch Leerraum sind (\t \n \v \f \r).
      const sauber = value.replace(/[\u0000-\u001f\u007f]/g, '');
      // A value that looks like an e-mail address is not a category.
      if (TRACK_EMAIL_LIKE.test(sauber)) continue;
      flach[key] = sauber.slice(0, TRACK_MAX_VALUE_LENGTH);
    } else if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
      flach[key] = value;
    } else {
      // Everything else — objects, arrays, null, undefined, functions — is content or noise.
      continue;
    }
    if (!outsideBudget) gezaehlt += 1;
  }
  return flach;
}
